import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/customFetch';

export type User = {
  id: string;
  email: string;
  emailConfirmed: boolean;
  permissions: string[];
  // Opcional no tipo porque um backend anterior a V2 não manda o campo.
  features?: Record<string, boolean>;
};

export type LoginResult =
  | { ok: true }
  | { ok: false; reason: 'mfa-required' | 'mfa-setup-required'; pendingToken: string }
  | { ok: false; reason: 'unconfirmed' | 'invalid' | 'use-sso' };

type UserContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  isFeatureEnabled: (feature: string) => boolean;
};

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const me = await api.get<User>('/auth/me');
      setUser(me);
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const login: UserContextValue['login'] = async (email, password) => {
    try {
      // Limpa qualquer dado cacheado de uma sessão anterior antes de logar.
      queryClient.clear();
      const body = await api.post<{ state: string; pendingToken: string }>(
        '/auth/login',
        { email, password },
      );
      if (body.state === 'mfa-setup-required') {
        return { ok: false, reason: 'mfa-setup-required', pendingToken: body.pendingToken };
      }
      if (body.state === 'mfa-required') {
        return { ok: false, reason: 'mfa-required', pendingToken: body.pendingToken };
      }
      // Fallback defensivo: backend pós-J4 nunca devolve "ok" diretamente — sessão
      // real só vem de /auth/mfa/verify-pending.
      await refresh();
      return { ok: true };
    } catch (err) {
      if (err instanceof ApiError) {
        const code = err.problem.title ?? '';
        if (err.status === 403 && code === 'User.UseSso') {
          return { ok: false, reason: 'use-sso' };
        }
        if (err.status === 403 && code === 'User.EmailNotConfirmed') {
          return { ok: false, reason: 'unconfirmed' };
        }
      }
      return { ok: false, reason: 'invalid' };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      // Descarta qualquer dado cacheado pelo TanStack Query ligado à sessão anterior.
      queryClient.clear();
    }
  };

  const hasPermission = (permission: string): boolean =>
    user?.permissions.includes(permission) ?? false;

  // Falha fechado: sem o bloco `features` (backend anterior a V2) a feature
  // some da UI em vez de aparecer quebrada.
  const isFeatureEnabled = (feature: string): boolean =>
    user?.features?.[feature] ?? false;

  return (
    <UserContext.Provider
      value={{ user, loading, login, logout, refresh, hasPermission, isFeatureEnabled }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser deve ser usado dentro de UserProvider');
  return ctx;
}
